import type { ReactNode } from "react";
import { Divider } from "@freightsimple/ui-core/ComponentLayout/Divider";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import ShipmentSection from "@/Shared/Shipment/CustomComponents/ShipmentSection";

interface ClaimsFormLayoutProps {
  title: string;
  description: string;
  children: ReactNode;
}

/**
 * The whole submit-claim page: the title and microcopy, the rule under them,
 * and the card the form sits in — all in one column, so the rule is exactly as
 * wide as the card beneath it, and the heading starts at the card's left edge.
 *
 * This screen does not render through `FullWidthLayout` like its neighbours.
 * That frame draws its rule across the full window, which no amount of capping
 * the content can pull back to the card's width — and narrowing it there would
 * change the header of the fourteen other screens that share it.
 *
 * The card itself is `ShipmentSection` — the same bordered box the shipment
 * page draws around Details and Documents — rather than a restatement of its
 * border, radius and shadow, so the two pages can't drift apart. It is passed
 * no title because the header above already carries one.
 *
 * 824px is the booking flow's default section width, so the two customer forms
 * measure the same instead of each picking a number.
 */
export function ClaimsFormLayout(props: ClaimsFormLayoutProps) {
  return (
    <div className="h-full w-full pt-12">
      <div className="mx-auto w-full max-w-[824px]">
        <Stack gap={4} align="stretch">
          <Stack gap={0} align="stretch">
            <Heading level={1}>{props.title}</Heading>
            <T size="base" color="secondary">
              {props.description}
            </T>
          </Stack>
          <Divider />
          <ShipmentSection id="submit-claim-form" title={undefined}>
            {props.children}
          </ShipmentSection>
        </Stack>
      </div>
    </div>
  );
}
